import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { isCapacitor } from '../lib/platform';

const isExcludedPath = (path) => {
  if (!path) return false;
  if (path.startsWith('/news/') || path.startsWith('/t/') || path.startsWith('/terms') || path.startsWith('/privacy')) return true;
  if (path === '/login-authentication' || path === '/login' || path.endsWith('/login')) return true;
  if (path.startsWith('/auth/')) return true;
  if (path === '/forgot-password' || path === '/reset-password') return true;
  return false;
};

/**
 * PWA Install Prompt — affiche un popup sur mobile (navigateur) pour inviter à installer l'app.
 *
 * Fonctionne sur :
 * - Chrome/Edge Android : intercepte l'événement beforeinstallprompt
 * - Safari iOS : affiche un guide manuel (Share → Add to Home Screen)
 * - Ne s'affiche PAS si l'app est déjà installée (standalone)
 * - Se ferme et ne réapparaît pas avant 7 jours si l'utilisateur dismiss
 */

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DAYS = 7;

const PwaInstallPrompt = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show in native app (Capacitor)
    if (isCapacitor()) return;

    // Initial path filter (mount-time check)
    if (isExcludedPath(window.location.pathname)) return;

    // Don't show if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    // Don't show on desktop
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile) return;

    // Don't show if dismissed recently
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) return;
    }

    // iOS detection
    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // iOS doesn't fire beforeinstallprompt — show manual guide after delay
      const timer = setTimeout(() => setShowPrompt(true), 2000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  // Suppress on every render if current route is excluded (covers post-mount navigation)
  if (!showPrompt || isExcludedPath(location?.pathname)) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 md:hidden rounded-2xl p-4 flex flex-col gap-3 bg-card border border-border"
      style={{
        boxShadow: '0 -4px 30px rgba(0,0,0,0.15)',
        zIndex: 9999,
        animation: 'slideUp 0.3s ease forwards',
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center gap-3">
        <img src="/logo-noirock.png" alt="NoiRock" className="w-10 h-10 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-foreground" style={{ fontFamily: 'Sora, sans-serif' }}>
            {t('pwa.installTitle')}
          </p>
          <p className="text-xs text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('pwa.installSubtitle')}
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 bg-muted text-muted-foreground hover:bg-muted/80"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Action */}
      {isIOS ? (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-muted border border-border">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
            <path d="M12 5v14M5 12l7-7 7 7" />
            <rect x="3" y="19" width="18" height="2" rx="1" />
          </svg>
          <p className="text-xs leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
            <Trans i18nKey="pwa.iosShareGuide" components={{ strong: <strong className="text-foreground" /> }} />
          </p>
        </div>
      ) : (
        <button
          onClick={handleInstall}
          className="w-full py-2.5 rounded-xl text-sm font-bold transition-all bg-primary text-primary-foreground hover:bg-primary/90"
          style={{ fontFamily: 'Sora, sans-serif' }}
        >
          {t('pwa.installButton')}
        </button>
      )}
    </div>
  );
};

export default PwaInstallPrompt;
